<template>
  <v-container>
    <v-card class="mb-2">
      <v-card-title>{{ account.first_name }} {{ account.last_name }} <CountryFlags v-if="account?.country" :countryCode="account.country" /> {{ account.index }} </v-card-title>
      <v-card-text>
        <div v-if="isLoggedIn"><b>ID:</b> {{ account.id }}</div>
        <div v-if="account.vt_current"><b>VT:</b> {{ account.vt_current }}</div>
        <div v-if="showEmail"><b>Email:</b> {{ account.email }}</div>
        <div v-if="isLoggedIn && account"><b>{{ $t('general.last_activity') }}:</b> {{ $formatDateTime(account.last_activity) }}</div>
        <div v-if="isLoggedIn && account.permissions && account.permissions.length > 0">
          <b>{{ $t('profile.permissions') }}: </b>
          <span v-for="(permission, index) in account.permissions" :key="permission">
            {{
              permission === 'club_chairman' ? $t("profile.chairman") :
                permission === 'organizer' ? $t("profile.organizer") :
                    permission === 'club_manager' ? $t("profile.club_manager") :
                        permission === 'manager' ? $t("profile.manager") :
                            permission === 'radio_control_manager' ? $t("profile.radio_control_manager") :
                              permission
            }}<span v-if="index < account.permissions.length - 1">, </span>
          </span>
        </div>

      </v-card-text>
    </v-card>
    <v-expansion-panels variant="accordion">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-icon class="mr-2">mdi-account-hard-hat</v-icon>
          {{ $t('profile.classes') }}
        </v-expansion-panel-title>
        <v-expansion-panel-text class="py-2">
          <RoleTable :account-id="accountId"/>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-icon class="mr-2">mdi-playlist-check</v-icon>
          {{ $t('profile.experience') }}
          <v-progress-circular v-if="loadingExperience" class="ml-3" indeterminate color="primary" />
          <span class="ml-3 text-h6" v-else>({{ experience.length }})</span>

        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-data-table
              density="compact"
              hide-default-footer="true"
              :headers="experienceHeaders"
              :items="experience"
              class="elevation-1"
              :items-per-page="-1"
              :sort-by="experienceSortBy"
              :loading="loadingExperience"
              loading-text="general.loading"
              no-data-text="Nenalezeny žádné záznamy o praxi"
              hover
          >
            <template v-slot:[`item.race_date`]="{ item }">
              {{ $formatDate(item.race_date) }}
            </template>
            <template v-slot:[`item.race_name`]="{ item }">
              {{ item.race_name }}
            </template>
            <template v-slot:[`item.race_level`]="{ item }">
              <span style="white-space: nowrap;">{{ RACE_LEVEL_DICT[item.race_level] }}</span>
            </template>
            <template v-slot:[`item.position`]="{ item }">
              <span style="white-space: nowrap;">{{ item.position }}</span>
            </template>
          </v-data-table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel readonly="readonly" expand-icon="" class="bg-orange-lighten-4">
        <v-expansion-panel-title>
          <v-icon class="mr-2">mdi-trophy</v-icon>
          {{ $t('profile.my_events') }}
        </v-expansion-panel-title>
        <v-data-table 
          density="compact" 
          :headers="headers" 
          :items="entries" 
          class="elevation-1" 
          :items-per-page="50" 
          :sort-by="sortBy" 
          :loading="loading" 
          loading-text="general.loading"
          hover
        >
          <template v-slot:item="{ item }">
            <tr :class="{ 'upcoming': isFutureDate(item.race_date), 'canceled': item.event_canceled }">
              <td>
                {{
                  item.is_event ? $formatDate(item.race_date) + "&ndash;" + $formatDate(item.event_date_end) : $formatDate(item.race_date)
                }}
              </td>
              <td>
                <router-link :to="'/soutez/' + item.event_id">{{ item.event_name }}</router-link>
              </td>
              <td>{{ item.race_name }}</td>
              <td>{{ item.category_name }}</td>
              <td><span v-if="!item.is_event">{{ item.place ?? '---' }}</span></td>
              <td class="text-center">{{ item.result_vt }}</td>
              <td v-if="hasRankingPoints" class="text-right">
                <span v-if="!item.is_event">{{ item.ranking_points ?? '' }}</span>
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            {{ account.first_name }} ještě nikde nebyl<span v-if="account.gender == 'F'">a</span> :(
          </template>

        </v-data-table>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script setup>
import {ref, onMounted, computed, watch} from 'vue';
import apiClient from '/api.js';
import {useRoute} from 'vue-router';
import {useAuthStore} from '/auth.js';
import RoleTable from "@/components/shared/RoleTable.vue";
import { useI18n } from 'vue-i18n'
import { RACE_LEVEL_DICT } from '@/components/const/enums.js';
import CountryFlags from '@/components/shared/CountryFlags.vue';

const { t } = useI18n()
const authStore = useAuthStore();
const isLoggedIn = ref(authStore.isLoggedIn);
const route = useRoute();
const accountId = route.params.id; // Získání ID uživatele z URLconst account = ref({});
const entries = ref([]);
const account = ref({});
const hasRankingPoints = computed(() =>
  entries.value?.some(r => r.ranking_points !== null && r.ranking_points !== undefined)
);
const sortBy = [
  { key: 'race_date', order: 'desc' },
  { key: 'race_id', order: 'desc' },
];
const headers = ref ([
  {title: t('profile.date'), key: 'event_date_start? event_date_start : race_date', align: 'left', sortable: true},
  {title: t('profile.event_name'), key: 'event_name', align: 'left', minWidth:180, sortable: true},
  {title: t('profile.race_name'), key: 'race_name', align: 'left', minWidth:130, sortable: true},
  {title: t('profile.category'), key: 'category_name', align: 'left', sortable: false},
  {title: t('profile.place'), key: 'place', align: 'left', sortable: false},
  {title: 'VT', key: 'result_vt', width: 30, align: 'center', sortable: false},

]);

// Přidání sloupce s ranking_points, pokud existuje alespoň 1 záznam
watch(hasRankingPoints, (yes) => {
  if (!headers.value) return;
  const idx = headers.value.findIndex(h => h.key === 'ranking_points');
  if (yes && idx === -1) {
    // vlož hned za VT
    const vtIdx = headers.value.findIndex(h => h.key === 'result_vt');
    headers.value.splice(vtIdx + 1, 0, { title: t('profile.ranking_points'), key: 'ranking_points', align: 'end', sortable: false });
  } else if (!yes && idx !== -1) {
    headers.value.splice(idx, 1);
  }
}, { immediate: true });

const loading = ref(true);

const showEmail = computed(() => authStore.isLoggedIn && account.value.allow_email); // pravidlo pro viditelnost emailu

const isFutureDate = (dateString) => {
  const currentDate = new Date();
  const raceDate = new Date(dateString);
  return raceDate > currentDate;
};

// Načítání údajů účtu
const loadAccountData = async (accountId) => {
  try {
    const response = await apiClient.get(`account/?id=${accountId}`);
    account.value = response.data[0];
  } catch (error) {
    console.error("There was an error fetching the account data:", error);
  }
};

// Načítání entries uživatele
const loadUserEntries = async (accountId) => {
  try {
    const response = await apiClient.get(`account/${accountId}/entries`);
    entries.value = response.data
  } catch (error) {
    console.error('Chyba při načítání entries:', error);
  } finally {
    loading.value = false;  // Nastavíme loading na false po načtení dat
  }

};


const experienceHeaders = [
  {title: t('profile.date'), key: 'race_date', align: 'left', sortable: true},
  {title: t('profile.race_name'), key: 'race_name', minWidth:200, align: 'left', sortable: true},
  {title: t('profile.lvl'), key: 'race_level', align: 'left', sortable: false},
  {title: t('profile.position'), key: 'position', align: 'left', sortable: false},
];

const experienceSortBy = [{key: 'race_date', order: 'desc'}]; // Klíč pro řazení

const loadingExperience = ref(true);
const experience = ref([]);

// Načítání entries uživatele
const loadUserExperience = async (accountId) => {
  loadingExperience.value = true;
  try {
    const response = await apiClient.get(`account/${accountId}/experience`);
    experience.value = response.data
  } catch (error) {
    console.error('Chyba při načítání praxe:', error);
  } finally {
    loadingExperience.value = false;  // Nastavíme loading na false po načtení dat
  }

};


// Sledujeme změny v ID uživatele a načítáme nová data při změně URL
watch(
    () => route.params.id, // Sledujeme změny route.params.id
    (newId) => {
      loadAccountData(newId); // Zavoláme funkci s novým ID
      loadUserEntries(newId);
      loadUserExperience(newId);
    }
);

onMounted(() => {
  loadAccountData(accountId);
  loadUserEntries(accountId);
  loadUserExperience(accountId);
});


</script>


<style scoped>
.upcoming {
  background-color: rgb(var(--v-theme-calendar-entry)) !important;
}

.canceled {
  text-decoration: line-through;
}

:deep() .v-expansion-panel-text__wrapper {
  padding: 0px;
}

</style>
